import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import SelectableCard from "./SelectableCard";
import AddTreatmentModal from "./AddTreatmentModal";
import ClinicalReferenceSources from "./ClinicalReferenceSources";

// ============================================================================
// Types
// ============================================================================

export interface Treatment {
  id: string;
  name: string;
  category:
    | "medication"
    | "procedure"
    | "therapy"
    | "lifestyle"
    | "referral"
    | "other";
  priority: "first-line" | "alternative" | "adjunct";
  urgency?: "stat" | "urgent" | "routine";
  rationale: string;
  diagnosis?: string | string[];
  route?: string; // For medications
  contraindications?: string[];
  mutuallyRedundantGroup?: string; // Group ID for "Pick N" groups
  pickCount?: number; // How many to pick from the group
  linkedDiagnoses: string[];
}

export interface TreatmentPlanData {
  treatments: Treatment[];
  treatmentStrategy?: string;
  considerations?: string[];
}

interface TreatmentPlanStepProps {
  selectedDiagnoses: any[];
  planData: TreatmentPlanData | null;
  loading: boolean;
  error: string | null;
  hasTreatments: boolean;
  retry: () => void;
  disabledTreatmentIds: Set<string>;
  onToggleTreatmentDisabled: (id: string) => void;
  selectedTreatments: Set<string>;
  onToggleTreatmentSelected: (id: string) => void;
  customTreatments?: Treatment[];
  onAddCustomTreatment?: (treatment: Treatment) => void;
  clinicalReferences?: any[];
  embeddedDocuments?: any[];
  /** Heading override, e.g. for MorningReport's teaching cases. */
  heading?: { title: string; subtitle: string };
  /** Puts a pencil on each card, as the Tests step does for results (inpatient Daily Plan notes). */
  onEditTreatment?: (treatment: Treatment) => void;
  editTooltip?: string;
  /** Extra content inside each card, e.g. the Daily Plan note once one is entered. */
  renderTreatmentNote?: (treatment: Treatment) => React.ReactNode;
}

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_LABELS: Record<string, string> = {
  other: "Diagnostics",
  medication: "Medications",
  procedure: "Procedures",
  therapy: "Therapies",
  lifestyle: "Lifestyle & Supportive Care",
  referral: "Referrals & Consultations",
};

const CATEGORY_ORDER = ["other", "medication", "procedure", "therapy", "lifestyle", "referral"];

// ============================================================================
// Treatment Card Component
// ============================================================================

interface TreatmentCardProps {
  treatment: Treatment;
  disabled: boolean;
  selected: boolean;
  onToggleDisabled: () => void;
  onToggleSelected: () => void;
  onEdit?: () => void;
  editTooltip?: string;
  note?: React.ReactNode;
}

const TreatmentCard: React.FC<TreatmentCardProps> = ({
  treatment,
  disabled,
  selected,
  onToggleDisabled,
  onToggleSelected,
  onEdit,
  editTooltip,
  note,
}) => {
  const chips: Array<{
    label: string;
    color?:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
    variant?: "filled" | "outlined";
  }> = [];

  if (treatment.category === "medication" && treatment.route) {
    chips.push({
      label: treatment.route,
      color: "default",
      variant: "outlined",
    });
  }
  if (treatment.urgency && treatment.urgency !== "routine") {
    chips.push({
      label: treatment.urgency === "stat" ? "STAT" : "Urgent",
      color: treatment.urgency === "stat" ? "error" : "warning",
      variant: "filled",
    });
  }

  const contraindications =
    treatment.contraindications && treatment.contraindications.length > 0 ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
        <WarningAmberRounded sx={{ fontSize: 16, color: "warning.main" }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          Contraindications: {treatment.contraindications.join(", ")}
        </Typography>
      </Box>
    ) : null;

  return (
    <SelectableCard
      title={treatment.name}
      rationale={treatment.rationale}
      selectionVariant="checkbox"
      selected={selected}
      disabled={disabled}
      onClick={onToggleSelected}
      onToggleDisabled={onToggleDisabled}
      hideTooltip="Hide"
      chips={chips}
      onEdit={onEdit}
      editTooltip={editTooltip}
      additionalContent={
        contraindications || note ? (
          <>
            {contraindications}
            {note}
          </>
        ) : null
      }
    />
  );
};

// ============================================================================
// Main Component
// ============================================================================

const TreatmentPlanStep: React.FC<TreatmentPlanStepProps> = ({
  selectedDiagnoses,
  planData,
  loading,
  error,
  hasTreatments,
  retry,
  disabledTreatmentIds,
  onToggleTreatmentDisabled,
  selectedTreatments,
  onToggleTreatmentSelected,
  customTreatments = [],
  onAddCustomTreatment,
  clinicalReferences = [],
  embeddedDocuments = [],
  heading,
  onEditTreatment,
  editTooltip,
  renderTreatmentNote,
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);

  const renderCard = (treatment: Treatment) => (
    <TreatmentCard
      key={treatment.id}
      treatment={treatment}
      disabled={disabledTreatmentIds.has(treatment.id)}
      selected={selectedTreatments.has(treatment.id)}
      onToggleDisabled={() => onToggleTreatmentDisabled(treatment.id)}
      onToggleSelected={() => onToggleTreatmentSelected(treatment.id)}
      // Only a checked item can carry a note: the note request lists checked items only, so a
      // note on an unchecked one would show on the card and never reach the chart.
      onEdit={
        onEditTreatment && selectedTreatments.has(treatment.id) && !disabledTreatmentIds.has(treatment.id)
          ? () => onEditTreatment(treatment)
          : undefined
      }
      editTooltip={editTooltip}
      note={
        selectedTreatments.has(treatment.id) && !disabledTreatmentIds.has(treatment.id)
          ? renderTreatmentNote?.(treatment)
          : null
      }
    />
  );
  // Group treatments by category
  const treatmentsByCategory = useMemo(() => {
    if (!planData?.treatments) return new Map<string, Treatment[]>();

    const grouped = new Map<string, Treatment[]>();
    for (const treatment of planData.treatments) {
      const category = treatment.category || "other";
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(treatment);
    }
    return grouped;
  }, [planData?.treatments]);

  // Get mutually redundant group info
  const mutuallyRedundantGroups = useMemo(() => {
    const groups: Record<string, { treatments: Treatment[]; pickCount: number }> = {};

    planData?.treatments.forEach((treatment) => {
      if (treatment.mutuallyRedundantGroup) {
        const groupId = treatment.mutuallyRedundantGroup;
        if (!groups[groupId]) {
          groups[groupId] = {
            treatments: [],
            pickCount: treatment.pickCount || 1,
          };
        }
        groups[groupId].treatments.push(treatment);
      }
    });
    return groups;
  }, [planData?.treatments]);

  const existingTreatmentNames = useMemo(() => [
    ...(planData?.treatments || []),
    ...customTreatments,
  ].map((t) => t.name), [planData?.treatments, customTreatments]);

  // No diagnoses selected
  if (selectedDiagnoses.length === 0) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Treatment Plan
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Select a diagnosis first to generate a treatment plan.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1.5 }}>
          {heading?.title ?? "Treatment Plan"}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {heading?.subtitle ??
          "Click treatments to select them for your plan. Use the hide button to exclude treatments from documentation."}
        </Typography>
      </Box>

      {/* Add Treatment button — only after loading completes */}
      {!loading && (
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddRounded />}
            onClick={() => setAddModalOpen(true)}
          >
            Add Treatment
          </Button>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ width: "100%", mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={retry}
              startIcon={<RefreshRounded />}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Generating treatment plan...
          </Typography>
        </Box>
      )}

      {/* Custom Treatments — shown above AI-generated sections */}
      {!loading && customTreatments.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Your Treatments
          </Typography>
          {customTreatments.map(renderCard)}
        </Box>
      )}

      {/* Treatments */}
      {!loading && hasTreatments && planData && (
        <Box sx={{ width: "100%" }}>
          {/* Treatment strategy */}
          {planData.treatmentStrategy && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: (theme: any) =>
                  theme.palette.primary.main + "08",
                borderColor: "primary.light",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Treatment Strategy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {planData.treatmentStrategy}
              </Typography>
            </Paper>
          )}

          {/* Treatments by category */}
          {Array.from(treatmentsByCategory.entries()).sort(
            ([a], [b]) =>
              (CATEGORY_ORDER.indexOf(a) + 1 || CATEGORY_ORDER.length + 1) -
              (CATEGORY_ORDER.indexOf(b) + 1 || CATEGORY_ORDER.length + 1),
          ).map(
            ([category, treatments]) => {
              const label = CATEGORY_LABELS[category] || "Other";

              // Build ordered list of segments: ungrouped treatments and group clusters
              const segments: React.ReactNode[] = [];
              const renderedGroups = new Set<string>();

              treatments.forEach((treatment) => {
                const groupId = treatment.mutuallyRedundantGroup;

                if (groupId) {
                  if (renderedGroups.has(groupId)) return;
                  renderedGroups.add(groupId);

                  const groupInfo = mutuallyRedundantGroups[groupId];
                  const groupTreatments = treatments.filter(
                    (t) => t.mutuallyRedundantGroup === groupId,
                  );

                  segments.push(
                    <Box
                      key={`group-${groupId}`}
                      sx={{
                        mb: 2,
                        pl: 1.5,
                        borderLeft: "3px solid",
                        borderColor: "warning.main",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "warning.dark",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Choose {groupInfo.pickCount}
                      </Typography>
                      {groupTreatments.map(renderCard)}
                    </Box>,
                  );
                } else {
                  segments.push(renderCard(treatment));
                }
              });

              return (
                <Box key={category} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1.5 }}
                  >
                    {label}
                  </Typography>
                  {segments}
                </Box>
              );
            },
          )}

          {/* Considerations */}
          {planData.considerations && planData.considerations.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Clinical Considerations
              </Typography>
              <Box
                component="ul"
                sx={{ m: 0, pl: 2.5, color: "text.secondary" }}
              >
                {planData.considerations.map((item, idx) => (
                  <li key={idx}>
                    <Typography variant="body2">{item}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Source documents that backed the treatment plan */}
      {!loading && (
        <ClinicalReferenceSources
          clinicalReferences={clinicalReferences}
          embeddedDocuments={embeddedDocuments}
        />
      )}

      {/* Empty state */}
      {!loading && !hasTreatments && !error && customTreatments.length === 0 && (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", textAlign: "center", py: 2 }}
        >
          No treatments generated.
        </Typography>
      )}

      <AddTreatmentModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={(treatment) => {
          onAddCustomTreatment?.(treatment);
        }}
        existingNames={existingTreatmentNames}
      />
    </Box>
  );
};

export default TreatmentPlanStep;
